import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Download, Upload, RefreshCw, Save, Trash2, Check } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

const COLOR_KEYS = [
    ["--primary", "Culoare principală"],
    ["--primary-foreground", "Text pe principal"],
    ["--background", "Fundal"],
    ["--foreground", "Text"],
    ["--card", "Fundal card"],
    ["--muted", "Muted"],
    ["--muted-foreground", "Text muted"],
    ["--border", "Border"],
    ["--destructive", "Eroare"],
    ["--success", "Succes"],
];

// Convert HSL string "H S% L%" -> #rrggbb
const hslToHex = (hslStr) => {
    try {
        const parts = String(hslStr || "").trim().replace(/%/g, "").split(/\s+/).map(Number);
        if (parts.length < 3 || parts.some(isNaN)) return "#888888";
        const [h, s, l] = parts.map((v, i) => i === 0 ? v : v / 100);
        const k = n => (n + h / 30) % 12;
        const a = s * Math.min(l, 1 - l);
        const f = n => {
            const c = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
            return Math.round(255 * c).toString(16).padStart(2, "0");
        };
        return `#${f(0)}${f(8)}${f(4)}`;
    } catch { return "#888888"; }
};
const hexToHsl = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
    if (!m) return "0 0% 50%";
    const int = parseInt(m[1], 16);
    let r = ((int >> 16) & 255) / 255, g = ((int >> 8) & 255) / 255, b = (int & 255) / 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    let h = 0, s = 0, l = (mx + mn) / 2;
    if (mx !== mn) {
        const d = mx - mn;
        s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
        switch (mx) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
            default: break;
        }
        h /= 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export default function AdminTheme() {
    const { theme, updateTheme, reload } = useTheme();
    const [text, setText] = React.useState("");
    const [mode, setMode] = React.useState("builder");
    const [library, setLibrary] = React.useState([]);
    const [newName, setNewName] = React.useState("");
    const [uploadingFont, setUploadingFont] = React.useState(false);

    React.useEffect(() => { if (theme) setText(JSON.stringify(theme, null, 2)); }, [theme]);
    React.useEffect(() => { api.get("/admin/themes").then(r => setLibrary(r.data || [])); }, []);

    const loadLibrary = () => api.get("/admin/themes").then(r => setLibrary(r.data || []));

    const saveJson = async () => {
        try {
            const parsed = JSON.parse(text);
            await updateTheme(parsed);
            toast.success("Temă salvată și aplicată!");
        } catch (e) { toast.error("JSON invalid: " + e.message); }
    };

    const saveField = async (group, key, value) => {
        const patch = { [group]: { [key]: value } };
        await updateTheme(patch);
        reload();
    };

    const onColorChange = (key, hex) => {
        const hsl = hexToHsl(hex);
        saveField("colors", key, hsl);
    };

    const download = () => {
        const blob = new Blob([JSON.stringify(theme, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob); const a = document.createElement("a");
        a.href = url; a.download = `theme-${theme?.name?.replace(/\s+/g, "-") || "current"}.json`; a.click();
        URL.revokeObjectURL(url);
    };

    const uploadFile = (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const parsed = JSON.parse(reader.result);
                setText(JSON.stringify(parsed, null, 2));
                toast.success("Fișier încărcat. Click 'Salvează JSON' pentru aplicare.");
            } catch { toast.error("JSON invalid"); }
        };
        reader.readAsText(f);
    };

    const uploadFont = async (e, kind) => {
        const file = e.target.files?.[0]; if (!file) return;
        setUploadingFont(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const fontFamily = file.name.replace(/\.[^.]+$/, "").replace(/[^\w\- ]/g, "");
            // Inject @font-face dynamically & save into typography
            const css = `@font-face { font-family: '${fontFamily}'; src: url('${res.data.url}') format('${file.name.endsWith('.woff2') ? 'woff2' : file.name.endsWith('.woff') ? 'woff' : 'truetype'}'); font-display: swap; }`;
            const style = document.createElement("style"); style.innerHTML = css; document.head.appendChild(style);
            await saveField("typography", kind, `'${fontFamily}', system-ui, sans-serif`);
            toast.success("Font încărcat și aplicat!");
        } catch { toast.error("Eroare la upload font"); }
        setUploadingFont(false);
    };

    const resetDefault = async () => {
        if (!window.confirm("Resetează la tema default?")) return;
        const res = await api.post("/admin/theme/reset");
        setText(JSON.stringify(res.data, null, 2));
        reload();
        toast.success("Temă resetată");
    };

    const saveCurrent = async () => {
        if (!newName) { toast.error("Dă un nume temei"); return; }
        await api.post("/admin/themes", { name: newName, data: theme });
        setNewName(""); loadLibrary(); toast.success("Temă salvată în bibliotecă");
    };
    const activate = async (id) => {
        await api.post(`/admin/themes/${id}/activate`); reload(); toast.success("Temă activată");
    };
    const deleteTheme = async (id) => {
        if (!window.confirm("Sigur ștergi?")) return;
        await api.delete(`/admin/themes/${id}`); loadLibrary();
    };

    return (
        <div data-testid="admin-theme">
            <div className="overline mb-1">Administrare</div>
            <h1 className="font-heading text-3xl mb-2">Temă vizuală</h1>
            <p className="text-sm text-muted-foreground mb-6">Schimbă culorile și fonturile cu un click. Nu necesită cunoștințe tehnice.</p>

            <div className="flex gap-1 mb-6 border-b border-border">
                <button onClick={() => setMode("builder")} className={`px-4 py-2 text-sm font-medium ${mode === "builder" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}>Builder</button>
                <button onClick={() => setMode("json")} className={`px-4 py-2 text-sm font-medium ${mode === "json" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}>JSON avansat</button>
                <button onClick={() => setMode("library")} className={`px-4 py-2 text-sm font-medium ${mode === "library" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}>Bibliotecă teme</button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                <button onClick={download} className="btn-outline flex items-center gap-2" data-testid="theme-download"><Download className="h-4 w-4" /> Descarcă JSON</button>
                <label className="btn-outline cursor-pointer flex items-center gap-2" data-testid="theme-upload">
                    <Upload className="h-4 w-4" /> Încarcă JSON
                    <input type="file" accept="application/json" className="hidden" onChange={uploadFile} />
                </label>
                <button onClick={resetDefault} className="btn-outline flex items-center gap-2" data-testid="theme-reset"><RefreshCw className="h-4 w-4" /> Resetează la default</button>
            </div>

            {mode === "builder" && theme && (
                <div className="space-y-6 max-w-4xl">
                    <section className="card-soft p-5">
                        <h3 className="font-heading text-lg mb-4">Culori</h3>
                        <p className="text-xs text-muted-foreground mb-3">Click pe pătrat pentru a deschide paleta de culori.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {COLOR_KEYS.map(([k, l]) => {
                                const hex = hslToHex(theme.colors?.[k] || "0 0% 50%");
                                return (
                                    <div key={k} className="flex items-center gap-3">
                                        <input type="color" value={hex} onChange={(e) => onColorChange(k, e.target.value)}
                                               className="w-12 h-12 rounded-lg border border-border cursor-pointer"
                                               data-testid={`theme-color-picker-${k}`} />
                                        <div className="flex-1">
                                            <label className="text-xs text-muted-foreground">{l}</label>
                                            <input value={theme.colors?.[k] || ""} onChange={(e) => saveField("colors", k, e.target.value)}
                                                   className="input-soft !py-1 text-sm font-mono" data-testid={`theme-color-${k}`} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                    <section className="card-soft p-5">
                        <h3 className="font-heading text-lg mb-4">Tipografie</h3>
                        <div className="space-y-3">
                            {[["--font-sans", "Font text (corp)"], ["--font-heading", "Font titluri"], ["--font-mono", "Font mono"]].map(([k, l]) => (
                                <div key={k}>
                                    <label className="text-xs text-muted-foreground block">{l}</label>
                                    <div className="flex gap-2 items-center">
                                        <input defaultValue={theme.typography?.[k] || ""} onBlur={(e) => saveField("typography", k, e.target.value)} className="input-soft font-mono text-sm flex-1" />
                                        <label className="btn-outline cursor-pointer !py-2 text-sm whitespace-nowrap flex items-center gap-1">
                                            <Upload className="h-3 w-3" /> Încarcă font
                                            <input type="file" accept=".woff,.woff2,.ttf,.otf" className="hidden" onChange={(e) => uploadFont(e, k)} disabled={uploadingFont} />
                                        </label>
                                    </div>
                                </div>
                            ))}
                            <p className="text-xs text-muted-foreground">Formate acceptate: WOFF2, WOFF, TTF, OTF. Fontul încărcat se aplică imediat pe site.</p>
                        </div>
                    </section>
                    <section className="card-soft p-5">
                        <h3 className="font-heading text-lg mb-4">Rotunjire colțuri</h3>
                        <div>
                            <label className="text-xs text-muted-foreground">Rază default (ex: 0.25rem, 0.75rem, 1rem)</label>
                            <input defaultValue={theme.radius?.["--radius"] || "0.75rem"} onBlur={(e) => saveField("radius", "--radius", e.target.value)} className="input-soft" />
                        </div>
                    </section>
                </div>
            )}

            {mode === "json" && (
                <div>
                    <textarea value={text} onChange={(e) => setText(e.target.value)} rows="24" className="input-soft font-mono text-xs" data-testid="theme-editor" />
                    <button onClick={saveJson} className="btn-primary mt-4" data-testid="theme-save">Salvează și aplică</button>
                </div>
            )}

            {mode === "library" && (
                <div className="space-y-4 max-w-3xl">
                    <div className="card-soft p-5 flex gap-2 items-end">
                        <div className="flex-1">
                            <label className="text-xs text-muted-foreground block mb-1">Salvează tema curentă ca...</label>
                            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="ex: Tema Vara 2026" className="input-soft" />
                        </div>
                        <button onClick={saveCurrent} className="btn-primary flex items-center gap-2"><Save className="h-4 w-4" /> Salvează</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {library.length === 0 && <p className="col-span-full text-muted-foreground text-sm">Nu ai teme salvate încă.</p>}
                        {library.map(t => (
                            <div key={t.theme_id} className="card-soft p-4 flex items-start justify-between">
                                <div>
                                    <div className="font-medium">{t.name}</div>
                                    <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("ro-RO")}</div>
                                    <div className="flex gap-1 mt-2">
                                        {Object.entries(t.data?.colors || {}).slice(0, 4).map(([k, v]) => (
                                            <div key={k} className="w-5 h-5 rounded border border-border" style={{ background: `hsl(${v})` }} />
                                        ))}
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => activate(t.theme_id)} className="btn-ghost" title="Activează"><Check className="h-4 w-4 text-primary" /></button>
                                    <button onClick={() => deleteTheme(t.theme_id)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
